import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Pencil, MapPin, Phone, Mail, Building2, Calendar, Clock, Shield, Car } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

interface Props {
  project: any;
}

export const ProjectSiteInfoTab = ({ project }: Props) => {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});

  const openEdit = () => {
    setForm({
      site_address: project.site_address ?? "",
      site_city: project.site_city ?? "",
      site_state: project.site_state ?? "",
      site_zip: project.site_zip ?? "",
      site_access_notes: project.site_access_notes ?? "",
      site_contact_name: project.site_contact_name ?? "",
      site_contact_phone: project.site_contact_phone ?? "",
      site_contact_email: project.site_contact_email ?? "",
      work_hours_start: project.work_hours_start ?? "07:00",
      work_hours_end: project.work_hours_end ?? "17:00",
      work_days: project.work_days ?? "Mon-Fri",
      parking_instructions: project.parking_instructions ?? "",
      badge_required: project.badge_required ?? false,
      badge_instructions: project.badge_instructions ?? "",
      safety_requirements: project.safety_requirements ?? "",
      site_rules: project.site_rules ?? "",
      mobilization_date: project.mobilization_date ?? "",
      scheduled_completion: project.scheduled_completion ?? "",
      gc_name: project.gc_name ?? "",
      gc_contact_name: project.gc_contact_name ?? "",
      gc_contact_phone: project.gc_contact_phone ?? "",
      gc_po_number: project.gc_po_number ?? "",
      default_payment_terms: project.default_payment_terms ?? "net_30",
      default_retainage_pct: project.default_retainage_pct ?? 10,
      retainage_reduce_at_50: project.retainage_reduce_at_50 ?? false,
      per_unit_release_allowed: project.per_unit_release_allowed ?? false,
      billing_type: project.billing_type ?? "unit_based",
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const updates: Record<string, any> = {};
    Object.entries(form).forEach(([k, v]) => {
      updates[k] = v === "" ? null : v;
    });
    const { error } = await supabase.from("projects").update(updates as any).eq("id", project.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Site info updated");
    setEditOpen(false);
    qc.invalidateQueries({ queryKey: ["projects", project.id] });
  };

  const set = (key: string, val: any) => setForm((p) => ({ ...p, [key]: val }));
  const fmtDate = (d: string | null) => d ? format(new Date(d + "T00:00:00"), "MMMM d, yyyy") : "—";
  const fmtTime = (t: string | null) => {
    if (!t) return "—";
    const [h, m] = t.split(":");
    const hr = parseInt(h);
    const ampm = hr >= 12 ? "PM" : "AM";
    return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${ampm}`;
  };
  const paymentLabel = (t: string | null) => {
    const map: Record<string, string> = { net_30: "Net 30", net_15: "Net 15", net_45: "Net 45", net_60: "Net 60", due_on_receipt: "Due on Receipt" };
    return map[t ?? ""] ?? t ?? "—";
  };

  const fullAddress = [project.site_address, project.site_city, project.site_state].filter(Boolean).join(", ");
  const addressWithZip = fullAddress + (project.site_zip ? ` ${project.site_zip}` : "");

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={openEdit}>
          <Pencil className="mr-1 h-4 w-4" /> Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Location */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" /> Location
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {fullAddress ? (
              <>
                <p className="font-medium">{project.site_address ?? "—"}</p>
                <p>{[project.site_city, project.site_state].filter(Boolean).join(", ")} {project.site_zip ?? ""}</p>
                {fullAddress && (
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(addressWithZip)}`} target="_blank" rel="noreferrer"
                    className="text-primary text-xs hover:underline inline-flex items-center gap-1 mt-1">
                    📍 Open in Maps
                  </a>
                )}
              </>
            ) : <p className="text-muted-foreground">No address set</p>}
          </CardContent>
        </Card>

        {/* Site Contact */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" /> Site Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p className="font-medium">{project.site_contact_name || "—"}</p>
            {project.site_contact_phone && <p className="flex items-center gap-1">📞 {project.site_contact_phone}</p>}
            {project.site_contact_email && <p className="flex items-center gap-1">✉️ {project.site_contact_email}</p>}
            {!project.site_contact_name && !project.site_contact_phone && <p className="text-muted-foreground">No contact set</p>}
          </CardContent>
        </Card>

        {/* GC Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" /> GC Information
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p className="font-medium">{project.gc_name || "—"}</p>
            {project.gc_contact_name && <p>Contact: {project.gc_contact_name} {project.gc_contact_phone && `📞 ${project.gc_contact_phone}`}</p>}
            {project.gc_po_number && <p>GC PO#: <span className="font-mono">{project.gc_po_number}</span></p>}
            {!project.gc_name && <p className="text-muted-foreground">No GC info set</p>}
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" /> Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p>Mobilization: <span className="font-medium">{fmtDate(project.mobilization_date)}</span></p>
            <p>Completion: <span className="font-medium">{fmtDate(project.scheduled_completion)}</span></p>
            <p className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> {project.work_days ?? "Mon-Fri"} {fmtTime(project.work_hours_start)} – {fmtTime(project.work_hours_end)}
            </p>
          </CardContent>
        </Card>

        {/* Site Rules */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" /> Site Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {project.parking_instructions && <p>🅿️ Parking: {project.parking_instructions}</p>}
            {project.badge_required && <p>🪪 Badge: Required {project.badge_instructions ? `— ${project.badge_instructions}` : ""}</p>}
            {project.safety_requirements && <p>🦺 Safety: {project.safety_requirements}</p>}
            {project.site_rules && <p>📋 Notes: {project.site_rules}</p>}
            {!project.parking_instructions && !project.badge_required && !project.safety_requirements && !project.site_rules && (
              <p className="text-muted-foreground">No site rules set</p>
            )}
          </CardContent>
        </Card>

        {/* Contract Defaults */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Car className="h-4 w-4 text-muted-foreground" /> Contract Defaults
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
             <p>Billing Type: <span className="font-medium capitalize">{(project.billing_type ?? "unit_based").replace("_", " ")}</span></p>
             <p>Payment Terms: <span className="font-medium">{paymentLabel(project.default_payment_terms)}</span></p>
             <p>Retainage: <span className="font-medium">{project.default_retainage_pct ?? 10}%</span></p>
             <p>Apply to all new WOs: <span className="font-medium">✓</span></p>
             {project.retainage_reduce_at_50 && <p>Reduce to 5% at 50% completion: <span className="font-medium">✓</span></p>}
             {project.per_unit_release_allowed && <p>Per-unit release allowed: <span className="font-medium">✓</span></p>}
           </CardContent>
        </Card>
      </div>

      {/* Edit Sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Site Information</SheetTitle>
          </SheetHeader>
          <div className="space-y-6 py-4">
            {/* Location */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Location</h3>
              <div><Label>Street Address</Label><Input value={form.site_address} onChange={(e) => set("site_address", e.target.value)} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>City</Label><Input value={form.site_city} onChange={(e) => set("site_city", e.target.value)} /></div>
                <div><Label>State</Label><Input value={form.site_state} onChange={(e) => set("site_state", e.target.value)} /></div>
                <div><Label>Zip</Label><Input value={form.site_zip} onChange={(e) => set("site_zip", e.target.value)} /></div>
              </div>
              <div><Label>Access Notes</Label><Textarea value={form.site_access_notes} onChange={(e) => set("site_access_notes", e.target.value)} rows={2} /></div>
            </div>

            {/* Site Contact */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Site Contact</h3>
              <div><Label>Name</Label><Input value={form.site_contact_name} onChange={(e) => set("site_contact_name", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Phone</Label><Input value={form.site_contact_phone} onChange={(e) => set("site_contact_phone", e.target.value)} /></div>
                <div><Label>Email</Label><Input value={form.site_contact_email} onChange={(e) => set("site_contact_email", e.target.value)} /></div>
              </div>
            </div>

            {/* GC Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">GC Information</h3>
              <div><Label>GC Name</Label><Input value={form.gc_name} onChange={(e) => set("gc_name", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Contact Name</Label><Input value={form.gc_contact_name} onChange={(e) => set("gc_contact_name", e.target.value)} /></div>
                <div><Label>Contact Phone</Label><Input value={form.gc_contact_phone} onChange={(e) => set("gc_contact_phone", e.target.value)} /></div>
              </div>
              <div><Label>GC PO Number</Label><Input value={form.gc_po_number} onChange={(e) => set("gc_po_number", e.target.value)} /></div>
            </div>

            {/* Schedule */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Schedule</h3>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Mobilization Date</Label><Input type="date" value={form.mobilization_date} onChange={(e) => set("mobilization_date", e.target.value)} /></div>
                <div><Label>Scheduled Completion</Label><Input type="date" value={form.scheduled_completion} onChange={(e) => set("scheduled_completion", e.target.value)} /></div>
              </div>
              <div><Label>Work Days</Label><Input value={form.work_days} onChange={(e) => set("work_days", e.target.value)} placeholder="Mon-Fri" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Start Time</Label><Input type="time" value={form.work_hours_start} onChange={(e) => set("work_hours_start", e.target.value)} /></div>
                <div><Label>End Time</Label><Input type="time" value={form.work_hours_end} onChange={(e) => set("work_hours_end", e.target.value)} /></div>
              </div>
            </div>

            {/* Site Rules */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Site Rules</h3>
              <div><Label>Parking Instructions</Label><Textarea value={form.parking_instructions} onChange={(e) => set("parking_instructions", e.target.value)} rows={2} /></div>
              <div className="flex items-center gap-3">
                <Switch checked={form.badge_required} onCheckedChange={(v) => set("badge_required", v)} />
                <Label>Badge Required</Label>
              </div>
              {form.badge_required && <div><Label>Badge Instructions</Label><Textarea value={form.badge_instructions} onChange={(e) => set("badge_instructions", e.target.value)} rows={2} /></div>}
              <div><Label>Safety Requirements</Label><Textarea value={form.safety_requirements} onChange={(e) => set("safety_requirements", e.target.value)} rows={2} /></div>
              <div><Label>Additional Site Rules</Label><Textarea value={form.site_rules} onChange={(e) => set("site_rules", e.target.value)} rows={2} /></div>
            </div>

            {/* Contract Defaults */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Contract Defaults</h3>
              <div>
                <Label>Payment Terms</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.default_payment_terms} onChange={(e) => set("default_payment_terms", e.target.value)}>
                  <option value="net_30">Net 30</option>
                  <option value="net_15">Net 15</option>
                  <option value="net_45">Net 45</option>
                  <option value="net_60">Net 60</option>
                  <option value="due_on_receipt">Due on Receipt</option>
                </select>
              </div>
              <div><Label>Retainage %</Label><Input type="number" value={form.default_retainage_pct} onChange={(e) => set("default_retainage_pct", parseFloat(e.target.value) || 0)} /></div>
              <div className="flex items-center gap-3">
                <Switch checked={form.retainage_reduce_at_50} onCheckedChange={(v) => set("retainage_reduce_at_50", v)} />
                <Label>Reduce to 5% at 50% completion</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.per_unit_release_allowed} onCheckedChange={(v) => set("per_unit_release_allowed", v)} />
                <Label>Per-unit release allowed</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
